import { Box, Typography } from '@mui/material';
import { useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';
import React, { useEffect, useEffectEvent, useMemo } from 'react';

import { useApolloError } from 'common/hooks/useApolloError';
import {
    IOfferRole,
    ITrainingRoleFilter,
    FeaturedArticlesDocument,
    HomePageOfferEventsDocument,
    HomepageTrainingsDocument,
} from 'graphql/types';
import { useFetchPages } from 'common/hooks/useFetchPages';
import { HomePageResults } from 'common/components/HomePageResults';

export interface IHomepageSectionStatus {
    loading: boolean;
    hasResults: boolean;
}

interface IProps {
    canUseOffers?: boolean;
    sectionTitle?: React.ReactNode;
    showFeaturedArticles?: boolean;
    trainingRole: ITrainingRoleFilter;
    trainer?: boolean;
    setStatus: (status: IHomepageSectionStatus) => void;
}

const PAGE_SIZE = 50;

export const HomePageSection = ({
    canUseOffers,
    sectionTitle,
    showFeaturedArticles,
    trainingRole,
    trainer,
    setStatus,
}: IProps) => {
    const { showApolloError } = useApolloError();

    const {
        data: featuredArticlesData,
        loading: featuredArticlesLoading,
        error: featuredArticlesError,
    } = useQuery(FeaturedArticlesDocument, {
        skip: !showFeaturedArticles,
        fetchPolicy: 'network-only',
    });
    const featuredArticles = featuredArticlesData?.featuredArticles || [];

    const onFeaturedArticlesError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (featuredArticlesError) {
            onFeaturedArticlesError(featuredArticlesError);
        }
    }, [featuredArticlesError]);

    const baseOfferEventsVars = useMemo(
        () => ({
            forRoles: trainer ? [IOfferRole.Trainer] : [IOfferRole.Enrolled],
            fromDate: new Date().toISOString(),
        }),
        [trainer]
    );

    const { data: offerEventsData, loading: offerEventsLoading } =
        useFetchPages({
            pagesToFetch: 2,
            pageSize: PAGE_SIZE,
            baseVariables: baseOfferEventsVars,
            document: HomePageOfferEventsDocument,
            selectPageInfo: (data) => data?.paginatedOfferEvents?.pageInfo,
            onError: showApolloError,
            skip: !canUseOffers,
            querySettings: {
                fetchPolicy: 'network-only',
            },
        });
    const baseTrainingVars = useMemo(
        () => ({
            role: trainer
                ? ITrainingRoleFilter.Trainer
                : ITrainingRoleFilter.Participant,
        }),
        [trainer]
    );

    const { data: trainingsData, loading: trainingsLoading } = useFetchPages({
        pagesToFetch: 2,
        pageSize: PAGE_SIZE,
        baseVariables: baseTrainingVars,
        document: HomepageTrainingsDocument,
        selectPageInfo: (data) => data?.overviewTrainings?.pageInfo,
        onError: showApolloError,
        querySettings: {
            fetchPolicy: 'network-only',
        },
    });

    const { paginatedOfferEvents } = offerEventsData || {};
    const offerEvents =
        paginatedOfferEvents?.edges
            ?.map((edge) => edge?.node)
            .filter(Boolean) || [];

    const { overviewTrainings } = trainingsData || {};
    const trainings =
        overviewTrainings?.edges?.map((edge) => edge?.node).filter(Boolean) ||
        [];

    const loading =
        offerEventsLoading || trainingsLoading || featuredArticlesLoading;

    const hasResults =
        !loading &&
        (!!offerEvents.length ||
            !!trainings.length ||
            !!featuredArticles.length);

    useEffect(() => {
        setStatus({ loading, hasResults });
    }, [loading, hasResults]);

    if (loading || !hasResults) return null;

    return (
        <Box sx={{ mb: 6 }}>
            <Box>
                {!!sectionTitle && (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            mb: 1.5,
                        }}
                    >
                        <Typography variant="h3">{sectionTitle}</Typography>
                    </Box>
                )}

                <HomePageResults
                    featuredArticles={featuredArticles}
                    offerEvents={offerEvents}
                    trainingRoleFilter={trainingRole}
                    trainings={trainings}
                />
            </Box>
        </Box>
    );
};
